import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import * as maptilersdk from '@maptiler/sdk'
import { markerCreateRequest } from '../../../../../action/marker-actions'
import { collapseLeftToolbar } from '../../../../../action/left-toolbar-state-actions'
import { MakeTextInput, NewMarkerIconStyle, GetHeader } from '../../../../threedmap/add-3dmap-items/add-marker/addHandlers'
import SetIdealWinds3D from '../../../common/hunt-wind/set-ideal-wind-3d'
import TooManyObjects from '../../common/tooMany-object'
import { showHideSubscriptionModal } from '../../../../../utils/subscriptionUtils'

const round6 = v => Math.round(v * 1e6) / 1e6

const DEFAULT_ICON = 'https://basemap-core.s3.us-west-2.amazonaws.com/markertypes/svg/Stand%20%28Ground%20Blind%29.svg'
const DEFAULT_COLOR = '9ECC3B'
const DEFAULT_TYPE_ID = 88

function buildMarkerSVG(iconUrl, hexColor) {
  return `<svg viewBox="0 0 16 16" width="32" height="32" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    <defs><clipPath id="add-stand-3d-clip"><circle cx="8" cy="6" r="5"/></clipPath></defs>
    <path d="M8,0C4.688,0,2,2.688,2,6c0,6,6,10,6,10s6-4,6-10C14,2.688,11.312,0,8,0z M8,8C6.344,8,5,6.656,5,5s1.344-3,3-3s3,1.344,3,3S9.656,8,8,8z" fill="${hexColor}"/>
    <circle cx="8" cy="6" r="5" fill="white"/>
    <image href="${iconUrl}" xlink:href="${iconUrl}" x="4" y="1.5" width="8" height="8" clip-path="url(#add-stand-3d-clip)"/>
  </svg>`
}

const AddStand3D = (props) => {
  const [step, setStep] = useState(1)
  const [markerData, setMarkerData] = useState(null)
  const [winds, setWinds] = useState([])
  const [saving, setSaving] = useState(false)
  const [standSaved, setStandSaved] = useState(false)
  const [tooManyObjects, setTooManyObjects] = useState(false)

  const [name, setName] = useState(`Hunt Stand - ${props.huntStands.length}`)
  const [notes, setNotes] = useState('')
  const [latitude, setLatitude] = useState('')
  const [longitude, setLongitude] = useState('')
  const [openStyleIcon, setOpenStyleIcon] = useState(false)
  const [colorMarker, setColorMarker] = useState(DEFAULT_COLOR)
  const [markerTypeId, setMarkerTypeId] = useState(DEFAULT_TYPE_ID)
  const [locationName, setLocationName] = useState('Stand (Blind)')
  const [iconUrl, setIconUrl] = useState(DEFAULT_ICON)
  const [markerType, setMarkerType] = useState(null)

  const markerRef = useRef(null)
  const iconUrlRef = useRef(DEFAULT_ICON)
  const colorRef = useRef(DEFAULT_COLOR)
  // Ref so saveStand always reads the latest winds without stale closure
  const windsRef = useRef([])

  const dropdownHeight = openStyleIcon ? 270 : 0

  const updateMarkerElement = (newIconUrl, newColor) => {
    if (!markerRef.current) return
    const el = markerRef.current.getElement()
    el.innerHTML = buildMarkerSVG(newIconUrl || iconUrlRef.current, '#' + (newColor || colorRef.current))
  }

  // Create the draggable stand marker once on mount
  useEffect(() => {
    const map = props.mapReferenceThreed
    if (!map) return

    const center = map.getCenter()
    const el = document.createElement('div')
    el.style.cursor = 'move'
    el.innerHTML = buildMarkerSVG(iconUrlRef.current, '#' + colorRef.current)

    const marker = new maptilersdk.Marker({
      element: el,
      draggable: true,
      anchor: 'bottom',
      pitchAlignment: 'viewport',
      rotationAlignment: 'viewport',
    })
      .setLngLat([center.lng, center.lat])
      .addTo(map)

    marker.on('drag', () => {
      const pos = marker.getLngLat()
      setLatitude(round6(pos.lat))
      setLongitude(round6(pos.lng))
    })

    markerRef.current = marker
    setLatitude(round6(center.lat))
    setLongitude(round6(center.lng))

    return () => {
      if (markerRef.current) {
        markerRef.current.remove()
        markerRef.current = null
      }
    }
  }, [props.mapReferenceThreed])

  // Attach map click handler only in step 1
  useEffect(() => {
    const map = props.mapReferenceThreed
    if (!map) return

    if (step !== 1) {
      map.getCanvas().style.cursor = ''
      return
    }

    map.getCanvas().style.cursor = 'crosshair'

    const handleClick = (e) => {
      const { lat, lng } = e.lngLat
      setLatitude(round6(lat))
      setLongitude(round6(lng))
      if (markerRef.current) markerRef.current.setLngLat([lng, lat])
    }

    map.on('click', handleClick)

    return () => {
      map.off('click', handleClick)
      map.getCanvas().style.cursor = ''
    }
  }, [props.mapReferenceThreed, step])

  const handleInputChange = (field, value) => {
    if (field === 'name') setName(value)
    else if (field === 'notes') setNotes(value)
    else if (field === 'latitude') setLatitude(value)
    else if (field === 'longitude') setLongitude(value)
  }

  const goToStep2 = () => {
    const currentMarkerType = markerType || {
      iconUrl: iconUrlRef.current,
      vectorIconUrl: iconUrlRef.current,
      name: locationName,
      usageFlag: 3,
    }
    const data = {
      altitude: 0,
      name,
      notes,
      latitude,
      longitude,
      userId: props.user.id,
      markerTypeId,
      color: colorRef.current,
      markerType: {
        iconUrl: iconUrlRef.current,
        name: currentMarkerType.name || locationName,
        vectorIconUrl: iconUrlRef.current,
        usageFlag: 3,
      },
      public: false,
    }

    const map = props.mapReferenceThreed
    if (map && latitude && longitude) {
      map.flyTo({
        center: [longitude, latitude],
        zoom: map.getZoom() < 17 ? 17 : map.getZoom(),
      })
    }

    setMarkerData(data)
    setStep(2)
  }

  const saveStand = async (latestWinds) => {
    const windsToSave = latestWinds || windsRef.current
    const toSave = {
      ...markerData,
      extendedData: `{"huntStandData": {"preferredWindDirections": ${JSON.stringify(windsToSave)}}}`,
    }
    setSaving(true)
    try {
      await props.markerCreateRequest(toSave)
      setStandSaved(true)
    } catch (err) {
      if (err.status === 400) {
        setTooManyObjects(true)
      } else {
        props.collapseLeftToolbar()
      }
    } finally {
      setSaving(false)
    }
  }

  const reset = () => {
    setStep(1)
    setMarkerData(null)
    setWinds([])
    windsRef.current = []
    setSaving(false)
    setStandSaved(false)
    setName(`Hunt Stand - ${props.huntStands.length}`)
    setNotes('')
  }

  const setWindsAndRef = (w) => {
    windsRef.current = w
    setWinds(w)
  }

  if (saving || standSaved) {
    return (
      <div style={{ padding: 10 }}>
        <div className='hunt-wind-header'>
          {standSaved ? 'STAND SAVED SUCCESSFULLY' : 'SAVING STAND'}
        </div>
        {standSaved && (
          <div style={{ display: 'flex' }}>
            <button style={{ width: '50%', backgroundColor: '#959595' }} onClick={props.collapseLeftToolbar}>
              CLOSE
            </button>
            <button style={{ width: '50%' }} onClick={reset}>NEW STAND</button>
          </div>
        )}
        {!standSaved && (
          <div style={{ width: '100%', marginBottom: 10 }}>
            <img className='horizontal-center dialog-spinner' src='../../../asset/loading-spinner.gif' />
          </div>
        )}
      </div>
    )
  }

  if (step === 2 && markerData) {
    return (
      <SetIdealWinds3D
        markerData={markerData}
        winds={winds}
        setWinds={setWindsAndRef}
        saveMarker={saveStand}
        setMarkerData={(val) => { if (val === null) setStep(1) }}
      />
    )
  }

  // Step 1: stand placement
  return (
    <>
      <GetHeader
        collapseLeftToolbar={props.collapseLeftToolbar}
        title='Add'
        titleName='Stand'
      />
      <div className='tools-content'>
        <div className='container-box'>
          <div className='container-box-two' style={{ overflowY: 'scroll', height: 'calc(100vh - 145px)' }}>
            <MakeTextInput
              onChange={handleInputChange}
              userPreferences={props.userPreferences}
              name='name'
              width='92%'
              containerWidth='100%'
              access='false'
              displayVal={name}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <MakeTextInput
                onChange={handleInputChange}
                userPreferences={props.userPreferences}
                name='latitude'
                width='85%'
                access='false'
                displayVal={String(latitude)}
              />
              <MakeTextInput
                onChange={handleInputChange}
                userPreferences={props.userPreferences}
                name='longitude'
                width='85%'
                access='false'
                displayVal={String(longitude)}
                marginLeft='10'
              />
            </div>

            <NewMarkerIconStyle
              dropdownHeight={dropdownHeight}
              iconProp={iconUrl}
              markerGroups={props.markerGroups}
              type='addStand'
              leftToolbarState={props.leftToolbarState}
              recentUsedMarkers={[]}
              selectedStyle='Hunt Stands'
              colorMarker={colorMarker}
              markerTypeId={markerTypeId}
              locationName={locationName}
              openStyleIcon={openStyleIcon}
              setOpenStyleIcon={setOpenStyleIcon}
              setSelectedStyle={() => {}}
              setFilterItems={() => {}}
              selectColor={(val) => {
                colorRef.current = val
                setColorMarker(val)
                updateMarkerElement(null, val)
              }}
              editable={null}
              setIconUrl={(value) => {
                iconUrlRef.current = value
                setIconUrl(value)
                updateMarkerElement(value, null)
              }}
              selectAnimal={(value) => {
                iconUrlRef.current = value.vectorIconUrl
                setMarkerType(value)
                setMarkerTypeId(value.id)
                setLocationName(value.name)
                updateMarkerElement(value.vectorIconUrl, null)
              }}
            />

            <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 20, marginTop: 10 }}>
              <label htmlFor='notes' className='label-style' style={{ marginBottom: 10 }}>Notes:</label>
              <textarea
                name='notes'
                id='notes'
                className='marker-notes'
                style={{ height: 70, background: '#333333', borderRadius: 5, border: 'none', width: '99%' }}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div style={{ width: '95%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', padding: 10, marginTop: 10 }}>
          <button
            style={{ width: '40%', margin: 0, backgroundColor: 'grey' }}
            onClick={props.collapseLeftToolbar}
          >
            Cancel
          </button>
          <button
            style={{ width: '40%', margin: 0, backgroundColor: latitude ? '#589500' : 'grey' }}
            onClick={goToStep2}
            disabled={!latitude}
          >
            Next
          </button>
        </div>

        {tooManyObjects && (
          <TooManyObjects
            cancel={props.collapseLeftToolbar}
            upgrade={() => showHideSubscriptionModal(props.auth)}
          />
        )}
      </div>
    </>
  )
}

const mapStateToProps = state => ({
  mapReferenceThreed: state.mapReferenceThreed,
  user: state.user,
  auth: state.auth,
  leftToolbarState: state.leftToolbarState,
  markerGroups: state.markerGroups,
  userPreferences: state.userPreferences,
  huntStands: state.markers.filter(m => m && m.markerType.usageFlag === 3),
})

const mapDispatchToProps = dispatch => ({
  markerCreateRequest: marker => dispatch(markerCreateRequest(marker)),
  collapseLeftToolbar: () => dispatch(collapseLeftToolbar()),
})

export default connect(mapStateToProps, mapDispatchToProps)(AddStand3D)
